/**
 * @license AngularJS v1.7.5
 * (c) 2010-2018 Google, Inc. http://angularjs.org
 * License: MIT
 */
(function(window, angular) {'use strict';

    var $resourceMinErr = angular.$$minErr('$resource');

// Helper functions and regex to lookup a dotted path on an object
// stopping at undefined/null.  The path must be composed of ASCII
// identifiers (just like $parse)
    var MEMBER_NAME_REGEX = /^(\.[a-zA-Z_$@][0-9a-zA-Z_$@]*)+$/;

    function isValidDottedPath(path) {
        return (path != null && path !== '' && path !== 'hasOwnProperty' &&
            MEMBER_NAME_REGEX.test('.' + path));
    }

    function lookupDottedPath(obj, path) {
        if (!isValidDottedPath(path)) {
            throw $resourceMinErr('badmember', 'Dotted member path "@{0}" is invalid.', path);
        }
        var keys = path.split('.');
        for (var i = 0, ii = keys.length; i < ii && angular.isDefined(obj); i++) {
            var key = keys[i];
            obj = (obj !== null) ? obj[key] : undefined;
        }
        return obj;
    }

    /**
     * Create a shallow copy of an object and clear other fields from the destination
     */
    function shallowClearAndCopy(src, dst) {
        dst = dst || {};

        angular.forEach(dst, function(value, key) {
            delete dst[key];
        });

        for (var key in src) {
            if (src.hasOwnProperty(key) && !(key.charAt(0) === '$' && key.charAt(1) === '$')) {
                dst[key] = src[key];
            }
        }

        return dst;
    }

    /**
     * @ngdoc module
     * @name ngResource
     * @description
     *
     * The `ngResource` module provides interaction support with RESTful services
     * via the $resource service.
     *
     * See {@link ngResource.$resourceProvider} and {@link ngResource.$resource} for usage.
     */

    /**
     * @ngdoc provider
     * @name $resourceProvider
     *
     * @description
     *
     * Use `$resourceProvider` to change the default behavior of the {@link ngResource.$resource}
     * service.
     *
     * ## Dependencies
     * Requires the {@link ngResource } module to be installed.
     *
     */

    /**
     * @ngdoc service
     * @name $resource
     * @requires $http
     * @requires ng.$log
     * @requires $q
     * @requires ng.$timeout
     *
     * @description
     * A factory which creates a resource object that lets you interact with
     * [RESTful](http://en.wikipedia.org/wiki/Representational_State_Transfer) server-side data sources.
     *
     * The returned resource object has action methods which provide high-level behaviors without
     * the need to interact with the low level {@link ng.$http $http} service.
     *
     * Requires the {@link ngResource `ngResource`} module to be installed.
     *
     * By default, trailing slashes will be stripped from the calculated URLs,
     * which can pose problems with server backends that do not expect that
     * behavior.  This can be disabled by configuring the `$resourceProvider` like
     * this:
     *
     * ```js
     app.config(['$resourceProvider', function($resourceProvider) {
       // Don't strip trailing slashes from calculated URLs
       $resourceProvider.defaults.stripTrailingSlashes = false;
     }]);
     * ```
     *
     * @param {string} url A parameterized URL template with parameters prefixed by `:` as in
     *   `/user/:username`. If you are using a URL with a port number (e.g.
     *   `http://example.com:8080/api`), it will be respected.
     *
     *   If you are using a url with a suffix, just add the suffix, like this:
     *   `$resource('http://example.com/resource.json')` or `$resource('http://example.com/:id.json')`
     *   or even `$resource('http://example.com/resource/:resource_id.:format')`
     *   If the parameter before the suffix is empty, :resource_id in this case, then the `/.` will be
     *   collapsed down to a single `.`.  If you need this sequence to appear and not collapse then you
     *   can escape it with `/\.`.
     *
     * @param {Object=} paramDefaults Default values for `url` parameters. These can be overridden in
     *   `actions` methods. If a parameter value is a function, it will be called every time
     *   a param value needs to be obtained for a request (unless the param was overridden). The
     *   function will be passed the current data value as an argument.
     *
     *   Each key value in the parameter object is first bound to url template if present and then any
     *   excess keys are appended to the url search query after the `?`.
     *
     *   Given a template `/path/:verb` and parameter `{verb: 'greet', salutation: 'Hello'}` results in
     *   URL `/path/greet?salutation=Hello`.
     *
     *   If the parameter value is prefixed with `@`, then the value for that parameter will be
     *   extracted from the corresponding property on the `data` object (provided when calling actions
     *   with a request body).
     *   For example, if the `defaultParam` object is `{someParam: '@someProp'}` then the value of
     *   `someParam` will be `data.someProp`.
     *   Note that the parameter will be ignored, when calling a "GET" action method (i.e. an action
     *   method that does not accept a request body).
     *
     * @param {Object.<Object>=} actions Hash with declaration of custom actions that will be available
     *   in addition to the default set of resource actions (see below). If a custom action has the same
     *   key as a default action (e.g. `save`), then the default action will be *overwritten*, and not
     *   extended.
     *
     *   The declaration should be created in the format of {@link ng.$http#usage $http.config}:
     *
     *       {
     *         action1: {method:?, params:?, isArray:?, headers:?, ...},
     *         action2: {method:?, params:?, isArray:?, headers:?, ...},
     *         ...
     *       }
     *
     *   Where:
     *
     *   - **`action`** – {string} – The name of action. This name becomes the name of the method on
     *     your resource object.
     *   - **`method`** – {string} – Case insensitive HTTP method (e.g. `GET`, `POST`, `PUT`,
     *     `DELETE`, `JSONP`, etc).
     *   - **`params`** – {Object=} – Optional set of pre-bound parameters for this action. If any of
     *     the parameter value is a function, it will be called every time when a param value needs to
     *     be obtained for a request (unless the param was overridden). The function will be passed the
     *     current data value as an argument.
     *   - **`url`** – {string} – Action specific `url` override. The url templating is supported just
     *     like for the resource-level urls.
     *   - **`isArray`** – {boolean=} – If true then the returned object for this action is an array,
     *     see `returns` section.
     *   - **`transformRequest`** –
     *     `{function(data, headersGetter)|Array.<function(data, headersGetter)>}` –
     *     Transform function or an array of such functions. The transform function takes the http
     *     request body and headers and returns its transformed (typically serialized) version.
     *     By default, transformRequest will contain one function that checks if the request data is
     *     an object and serializes it using `angular.toJson`. To prevent this behavior, set
     *     `transformRequest` to an empty array: `transformRequest: []`
     *   - **`transformResponse`** –
     *     `{function(data, headersGetter, status)|Array.<function(data, headersGetter, status)>}` –
     *     Transform function or an array of such functions. The transform function takes the HTTP
     *     response body, headers and status and returns its transformed (typically deserialized)
     *     version.
     *     By default, transformResponse will contain one function that checks if the response looks
     *     like a JSON string and deserializes it using `angular.fromJson`. To prevent this behavior,
     *     set `transformResponse` to an empty array: `transformResponse: []`
     *   - **`cache`** – `{boolean|Cache}` – A boolean value or object created with
     *     {@link ng.$cacheFactory `$cacheFactory`} to enable or disable caching of the HTTP response.
     *     See {@link $http#caching $http Caching} for more information.
     *   - **`timeout`** – `{number}` – Timeout in milliseconds.<br />
     *     **Note:** In contrast to {@link ng.$http#usage $http.config}, {@link ng.$q promises} are
     *     **not** supported in `$resource`, because the same value would be used for multiple requests.
     *     If you are looking for a way to cancel requests, you should use the `cancellable` option.
     *   - **`cancellable`** – `{boolean}` – If true, the request made by a "non-instance" call will be
     *     cancelled (if not already completed) by calling `$cancelRequest()` on the call's return
     *     value. Calling `$cancelRequest()` for a non-cancellable or an already completed/cancelled
     *     request will have no effect.
     *   - **`withCredentials`** – `{boolean}` – Whether to set the `withCredentials` flag on the
     *     XHR object. See
     *     [XMLHttpRequest.withCredentials](https://developer.mozilla.org/en-US/docs/Web/API/XMLHttpRequest/withCredentials)
     *     for more information.
     *   - **`responseType`** – `{string}` – See
     *     [XMLHttpRequest.responseType](https://developer.mozilla.org/en-US/docs/Web/API/XMLHttpRequest/responseType).
     *   - **`interceptor`** – `{Object=}` – The interceptor object has four optional methods -
     *     `request`, `requestError`, `response`, and `responseError`. See
     *     {@link ng.$http#interceptors $http interceptors} for details. Note that
     *     `request`/`requestError` interceptors are applied before calling `$http`, thus before any
     *     global `$http` interceptors. Also, rejecting or throwing an error inside the `request`
     *     interceptor will result in calling the `responseError` interceptor.
     *     The resource instance or collection is available on the `resource` property of the
     *     `http response` object passed to `response`/`responseError` interceptors.
     *     Keep in mind that the associated promise will be resolved with the value returned by the
     *     response interceptors. Make sure you return an appropriate value and not the `response`
     *     object passed as input. For reference, the default `response` interceptor (which gets applied
     *     if you don't specify a custom one) returns `response.resource`.<br />
     *     See {@link ngResource.$resource#using-interceptors below} for an example of using
     *     interceptors in `$resource`.
     *   - **`hasBody`** – `{boolean}` – If true, then the request will have a body.
     *     If not specified, then only POST, PUT and PATCH requests will have a body. *
     * @param {Object} options Hash with custom settings that should extend the
     *   default `$resourceProvider` behavior.  The supported options are:
     *
     *   - **`stripTrailingSlashes`** – {boolean} – If true then the trailing
     *   slashes from any calculated URL will be stripped. (Defaults to true.)
     *   - **`cancellable`** – {boolean} – If true, the request made by a "non-instance" call will be
     *   cancelled (if not already completed) by calling `$cancelRequest()` on the call's return value.
     *   This can be overwritten per action. (Defaults to false.)
     *
     * @returns {Object} A resource "class" object with methods for the default set of resource actions
     *   optionally extended with custom `actions`. The default set contains these actions:
     *   ```js
     *   {
     *     'get':    {method: 'GET'},
     *     'save':   {method: 'POST'},
     *     'query':  {method: 'GET', isArray: true},
     *     'remove': {method: 'DELETE'},
     *     'delete': {method: 'DELETE'}
     *   }
     *   ```
     *
     *   Calling these methods invoke {@link ng.$http} with the specified http method, destination and
     *   parameters. When the data is returned from the server then the object is an instance of the
     *   resource class. The actions `save`, `remove` and `delete` are available on it as methods with
     *   the `$` prefix. This allows you to easily perform CRUD operations (create, read, update,
     *   delete) on server-side data like this:
     *   ```js
     *   var User = $resource('/user/:userId', {userId: '@id'});
     *   User.get({userId: 123}).$promise.then(function(user) {
     *     user.abc = true;
     *     user.$save();
     *   });
     *   ```
     *
     *   It is important to realize that invoking a `$resource` object method immediately returns an
     *   empty reference (object or array depending on `isArray`). Once the data is returned from the
     *   server the existing reference is populated with the actual data. This is a useful trick since
     *   usually the resource is assigned to a model which is then rendered by the view. Having an empty
     *   object results in no rendering, once the data arrives from the server then the object is
     *   populated with the data and the view automatically re-renders itself showing the new data. This
     *   means that in most cases one never has to write a callback function for the action methods.
     *
     *   The action methods on the class object or instance object can be invoked with the following
     *   parameters:
     *
     *   - "class" actions without a body: `Resource.action([parameters], [success], [error])`
     *   - "class" actions with a body: `Resource.action([parameters], postData, [success], [error])`
     *   - instance actions: `instance.$action([parameters], [success], [error])`
     *
     *
     *   When calling instance methods, the instance itself is used as the request body (if the action
     *   should have a body). By default, only actions using `POST`, `PUT` or `PATCH` have request
     *   bodies, but you can use the `hasBody` configuration option to specify whether an action
     *   should have a body or not (regardless of its HTTP method).
     *
     *
     *   Success callback is called with (value (Object|Array), responseHeaders (Function),
     *   status (number), statusText (string)) arguments, where `value` is the populated resource
     *   instance or collection object. The error callback is called with (httpResponse) argument.
     *
     *   Class actions return an empty instance (with the additional properties listed below).
     *   Instance actions return a promise for the operation.
     *
     *   The Resource instances and collections have these additional properties:
     *
     *   - `$promise`: The {@link ng.$q promise} of the original server interaction that created this
     *     instance or collection.
     *
     *     On success, the promise is resolved with the same resource instance or collection object,
     *     updated with data from server. This makes it easy to use in the
     *     {@link ngRoute.$routeProvider `resolve` section of `$routeProvider.when()`} to defer view
     *     rendering until the resource(s) are loaded.
     *
     *     On failure, the promise is rejected with the {@link ng.$http http response} object.
     *
     *     If an interceptor object was provided, the promise will instead be resolved with the value
     *     returned by the response interceptor (on success) or responceError interceptor (on failure).
     *
     *   - `$resolved`: `true` after first server interaction is completed (either with success or
     *      rejection), `false` before that. Knowing if the Resource has been resolved is useful in
     *      data-binding. If there is a response/responseError interceptor and it returns a promise,
     *      `$resolved` will wait for that too.
     *
     *   The Resource instances and collections have these additional methods:
     *
     *   - `$cancelRequest`: If there is a cancellable, pending request related to the instance or
     *      collection, calling this method will abort the request.
     *
     *   The Resource instances have these additional methods:
     *
     *   - `toJSON`: It returns a simple object without any of the extra properties added as part of
     *     the Resource API. This object can be serialized through {@link angular.toJson} safely
     *     without attaching AngularJS-specific fields. Notice that `JSON.stringify` (and
     *     `angular.toJson`) automatically use this method when serializing a Resource instance
     *     (see [MDN](https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/JSON/stringify#toJSON%28%29_behavior)).
     *
     * @example
     *
     * ### Basic usage
     *
     ```js
     // Define a CreditCard class
     var CreditCard = $resource('/users/:userId/cards/:cardId',
     {userId: 123, cardId: '@id'}, {
         charge: {method: 'POST', params: {charge: true}}
       });

     // We can retrieve a collection from the server
     var cards = CreditCard.query();
     // GET: /users/123/cards
     // server returns: [{id: 456, number: '1234', name: 'Smith'}]

     // Wait for the request to complete
     cards.$promise.then(function() {
       var card = cards[0];

       // Each item is an instance of CreditCard
       expect(card instanceof CreditCard).toEqual(true);

       // Non-GET methods are mapped onto the instances
       card.name = 'J. Smith';
       card.$save();
           // POST: /users/123/cards/456 {id: 456, number: '1234', name: 'J. Smith'}
           // server returns: {id: 456, number: '1234', name: 'J. Smith'}

       // Our custom method is mapped as well (since it uses POST)
       card.$charge({amount: 9.99});
           // POST: /users/123/cards/456?amount=9.99&charge=true {id: 456, number: '1234', name: 'J. Smith'}
     });

     // We can create an instance as well
     var newCard = new CreditCard({number: '0123'});
     newCard.name = 'Mike Smith';

     var savePromise = newCard.$save();
     // POST: /users/123/cards {number: '0123', name: 'Mike Smith'}
     // server returns: {id: 789, number: '0123', name: 'Mike Smith'}

     savePromise.then(function() {
       // Once the promise is resolved, the created instance
       // is populated with the data returned by the server
       expect(newCard.id).toEqual(789);
     });
     ```
     *
     * The object returned from a call to `$resource` is a resource "class" which has one "static"
     * method for each action in the definition.
     *
     * Calling these methods invokes `$http` on the `url` template with the given HTTP `method`,
     * `params` and `headers`.
     *
     * @example
     *
     * ### Accessing the response
     *
     * When the data is returned from the server then the object is an instance of the resource type and
     * all of the non-GET methods are available with `$` prefix. This allows you to easily support CRUD
     * operations (create, read, update, delete) on server-side data.
     *
     ```js
     var User = $resource('/users/:userId', {userId: '@id'});
     User.get({userId: 123}).$promise.then(function(user) {
       user.abc = true;
       user.$save();
     });
     ```
     *
     * It's worth noting that the success callback for `get`, `query` and other methods gets called with
     * the resource instance (populated with the data that came from the server) as well as an `$http`
     * header getter function, the HTTP status code and the response status text. So one could rewrite
     * the above example and get access to HTTP headers as follows:
     *
     ```js
     var User = $resource('/users/:userId', {userId: '@id'});
     User.get({userId: 123}, function(user, getResponseHeaders) {
       user.abc = true;
       user.$save(function(user, putResponseHeaders) {
         // `user` => saved `User` object
         // `putResponseHeaders` => `$http` header getter
       });
     });
     ```
     *
     * @example
     *
     * ### Creating custom actions
     *
     * In this example we create a custom method on our resource to make a PUT request:
     *
     ```js
     var app = angular.module('app', ['ngResource']);

     // Some APIs expect a PUT request in the format URL/object/ID
     // Here we are creating an 'update' method
     app.factory('Notes', ['$resource', function($resource) {
        return $resource('/notes/:id', {id: '@id'}, {
          update: {method: 'PUT'}
        });
      }]);

     // In our controller we get the ID from the URL using `$location`
     app.controller('NotesCtrl', ['$location', 'Notes', function($location, Notes) {
        // First, retrieve the corresponding `Note` object from the server
        // (Assuming a URL of the form `.../notes?id=XYZ`)
        var noteId = $location.search().id;
        var note = Notes.get({id: noteId});

        note.$promise.then(function() {
          note.content = 'Hello, world!';

          // Now call `update` to save the changes on the server
          Notes.update(note);
              // This will PUT /notes/ID with the note object as the request payload

          // Since `update` is a non-GET method, it will also be available on the instance
          // (prefixed with `$`), so we could replace the `Note.update()` call with:
          //note.$update();
        });
      }]);
     ```
     *
     * @example
     *
     * ### Cancelling requests
     *
     * If an action's configuration specifies that it is cancellable, you can cancel the request related
     * to an instance or collection (as long as it is a result of a "non-instance" call):
     *
     ```js
     // ...defining the `Hotel` resource...
     var Hotel = $resource('/api/hotels/:id', {id: '@id'}, {
       // Let's make the `query()` method cancellable
       query: {method: 'get', isArray: true, cancellable: true}
     });

     // ...somewhere in the PlanVacationController...
     ...
     this.onDestinationChanged = function onDestinationChanged(destination) {
       // We don't care about any pending request for hotels
       // in a different destination any more
       if (this.availableHotels) {
         this.availableHotels.$cancelRequest();
       }

       // Let's query for hotels in `destination`
       // (calls: /api/hotels?location=<destination>)
       this.availableHotels = Hotel.query({location: destination});
     };
     ```
     *
     * @example
     *
     * ### Using interceptors
     *
     * You can use interceptors to transform the request or response, perform additional operations, and
     * modify the returned instance/collection. The following example, uses `request` and `response`
     * interceptors to augment the returned instance with additional info:
     *
     ```js
     var Thing = $resource('/api/things/:id', {id: '@id'}, {
       save: {
         method: 'POST',
         interceptor: {
           request: function(config) {
             // Before the request is sent out, store a timestamp on the request config
             config.requestTimestamp = Date.now();
             return config;
           },
           response: function(response) {
             // Get the instance from the response object
             var instance = response.resource;

             // Augment the instance with a custom `saveLatency` property, computed as the time
             // between sending the request and receiving the response.
             instance.saveLatency = Date.now() - response.config.requestTimestamp;

             // Return the instance
             return instance;
           }
         }
       }
     });

     Thing.save({foo: 'bar'}).$promise.then(function(thing) {
       console.log('That thing was saved in ' + thing.saveLatency + 'ms.');
     });
     ```
     *
     */
    angular.module('ngResource', ['ng']).
    info({ angularVersion: '1.7.5' }).
    provider('$resource', function ResourceProvider() {
        var PROTOCOL_AND_IPV6_REGEX = /^https?:\/\/\[[^\]]*][^/]*/;

        var provider = this;

        /**
         * @ngdoc property
         * @name $resourceProvider#defaults
         * @description
         * Object containing default options used when creating `$resource` instances.
         *
         * The default values satisfy a wide range of usecases, but you may choose to overwrite any of
         * them to further customize your instances. The available properties are:
         *
         * - **stripTrailingSlashes** – `{boolean}` – If true, then the trailing slashes from any
         *   calculated URL will be stripped.<br />
         *   (Defaults to true.)
         * - **cancellable** – `{boolean}` – If true, the request made by a "non-instance" call will be
         *   cancelled (if not already completed) by calling `$cancelRequest()` on the call's return
         *   value. For more details, see {@link ngResource.$resource}. This can be overwritten per
         *   resource class or action.<br />
         *   (Defaults to false.)
         * - **actions** - `{Object.<Object>}` - A hash with default actions declarations. Actions are
         *   high-level methods corresponding to RESTful actions/methods on resources. An action may
         *   specify what HTTP method to use, what URL to hit, if the return value will be a single
         *   object or a collection (array) of objects etc. For more details, see
         *   {@link ngResource.$resource}. The actions can also be enhanced or overwritten per resource
         *   class.<br />
         *   The default actions are:
         *   ```js
         *   {
         *     get: {method: 'GET'},
         *     save: {method: 'POST'},
         *     query: {method: 'GET', isArray: true},
         *     remove: {method: 'DELETE'},
         *     delete: {method: 'DELETE'}
         *   }
         *   ```
         *
         * #### Example
         *
         * For example, you can specify a new `update` action that uses the `PUT` HTTP verb:
         *
         * ```js
         *   angular.
         *     module('myApp').
         *     config(['$resourceProvider', function ($resourceProvider) {
         *       $resourceProvider.defaults.actions.update = {
         *         method: 'PUT'
         *       };
         *     }]);
         * ```
         *
         * Or you can even overwrite the whole `actions` list and specify your own:
         *
         * ```js
         *   angular.
         *     module('myApp').
         *     config(['$resourceProvider', function ($resourceProvider) {
         *       $resourceProvider.defaults.actions = {
         *         create: {method: 'POST'},
         *         get:    {method: 'GET'},
         *         getAll: {method: 'GET', isArray:true},
         *         update: {method: 'PUT'},
         *         delete: {method: 'DELETE'}
         *       };
         *     });
         * ```
         *
         */
        this.defaults = {
            // Strip slashes by default
            stripTrailingSlashes: true,

            // Make non-instance requests cancellable (via `$cancelRequest()`)
            cancellable: false,

            // Default actions configuration
            actions: {
                'get': {method: 'GET'},
                'save': {method: 'POST'},
                'query': {method: 'GET', isArray: true},
                'remove': {method: 'DELETE'},
                'delete': {method: 'DELETE'}
            }
        };

        this.$get = ['$http', '$log', '$q', '$timeout', function($http, $log, $q, $timeout) {

            var noop = angular.noop,
                forEach = angular.forEach,
                extend = angular.extend,
                copy = angular.copy,
                isArray = angular.isArray,
                isDefined = angular.isDefined,
                isFunction = angular.isFunction,
                isNumber = angular.isNumber,
                encodeUriQuery = angular.$$encodeUriQuery,
                encodeUriSegment = angular.$$encodeUriSegment;

            function Route(template, defaults) {
                this.template = template;
                this.defaults = extend({}, provider.defaults, defaults);
                this.urlParams = {};
            }

            Route.prototype = {
                setUrlParams: function(config, params, actionUrl) {
                    var self = this,
                        url = actionUrl || self.template,
                        val,
                        encodedVal,
                        protocolAndIpv6 = '';

                    var urlParams = self.urlParams = Object.create(null);
                    forEach(url.split(/\W/), function(param) {
                        if (param === 'hasOwnProperty') {
                            throw $resourceMinErr('badname', 'hasOwnProperty is not a valid parameter name.');
                        }
                        if (!(new RegExp('^\\d+$').test(param)) && param &&
                            (new RegExp('(^|[^\\\\]):' + param + '(\\W|$)').test(url))) {
                            urlParams[param] = {
                                isQueryParamValue: (new RegExp('\\?.*=:' + param + '(?:\\W|$)')).test(url)
                            };
                        }
                    });
                    url = url.replace(/\\:/g, ':');
                    url = url.replace(PROTOCOL_AND_IPV6_REGEX, function(match) {
                        protocolAndIpv6 = match;
                        return '';
                    });

                    params = params || {};
                    forEach(self.urlParams, function(paramInfo, urlParam) {
                        val = params.hasOwnProperty(urlParam) ? params[urlParam] : self.defaults[urlParam];
                        if (isDefined(val) && val !== null) {
                            if (paramInfo.isQueryParamValue) {
                                encodedVal = encodeUriQuery(val, true);
                            } else {
                                encodedVal = encodeUriSegment(val);
                            }
                            url = url.replace(new RegExp(':' + urlParam + '(\\W|$)', 'g'), function(match, p1) {
                                return encodedVal + p1;
                            });
                        } else {
                            url = url.replace(new RegExp('(/?):' + urlParam + '(\\W|$)', 'g'), function(match,
                                                                                                        leadingSlashes, tail) {
                                if (tail.charAt(0) === '/') {
                                    return tail;
                                } else {
                                    return leadingSlashes + tail;
                                }
                            });
                        }
                    });

                    // strip trailing slashes and set the url (unless this behavior is specifically disabled)
                    if (self.defaults.stripTrailingSlashes) {
                        url = url.replace(/\/+$/, '') || '/';
                    }

                    // Collapse `/.` if found in the last URL path segment before the query.
                    // E.g. `http://url.com/id/.format?q=x` becomes `http://url.com/id.format?q=x`.
                    url = url.replace(/\/\.(?=\w+($|\?))/, '.');
                    // Replace escaped `/\.` with `/.`.
                    // (If `\.` comes from a param value, it will be encoded as `%5C.`.)
                    config.url = protocolAndIpv6 + url.replace(/\/(\\|%5C)\./, '/.');


                    // set params - delegate param encoding to $http
                    forEach(params, function(value, key) {
                        if (!self.urlParams[key]) {
                            config.params = config.params || {};
                            config.params[key] = value;
                        }
                    });
                }
            };


            function resourceFactory(url, paramDefaults, actions, options) {
                var route = new Route(url, options);

                actions = extend({}, provider.defaults.actions, actions);

                function extractParams(data, actionParams) {
                    var ids = {};
                    actionParams = extend({}, paramDefaults, actionParams);
                    forEach(actionParams, function(value, key) {
                        if (isFunction(value)) { value = value(data); }
                        ids[key] = value && value.charAt && value.charAt(0) === '@' ?
                            lookupDottedPath(data, value.substr(1)) : value;
                    });
                    return ids;
                }

                function defaultResponseInterceptor(response) {
                    return response.resource;
                }

                function Resource(value) {
                    shallowClearAndCopy(value || {}, this);
                }

                Resource.prototype.toJSON = function() {
                    var data = extend({}, this);
                    delete data.$promise;
                    delete data.$resolved;
                    delete data.$cancelRequest;
                    return data;
                };

                forEach(actions, function(action, name) {
                    var hasBody = action.hasBody === true || (action.hasBody !== false && /^(POST|PUT|PATCH)$/i.test(action.method));
                    var numericTimeout = action.timeout;
                    var cancellable = isDefined(action.cancellable) ?
                        action.cancellable : route.defaults.cancellable;

                    if (numericTimeout && !isNumber(numericTimeout)) {
                        $log.debug('ngResource:\n' +
                            '  Only numeric values are allowed as `timeout`.\n' +
                            '  Promises are not supported in $resource, because the same value would ' +
                            'be used for multiple requests. If you are looking for a way to cancel ' +
                            'requests, you should use the `cancellable` option.');
                        delete action.timeout;
                        numericTimeout = null;
                    }

                    Resource[name] = function(a1, a2, a3, a4) {
                        var params = {}, data, onSuccess, onError;

                        switch (arguments.length) {
                            case 4:
                                onError = a4;
                                onSuccess = a3;
                            // falls through
                            case 3:
                            case 2:
                                if (isFunction(a2)) {
                                    if (isFunction(a1)) {
                                        onSuccess = a1;
                                        onError = a2;
                                        break;
                                    }

                                    onSuccess = a2;
                                    onError = a3;
                                    // falls through
                                } else {
                                    params = a1;
                                    data = a2;
                                    onSuccess = a3;
                                    break;
                                }
                            // falls through
                            case 1:
                                if (isFunction(a1)) onSuccess = a1;
                                else if (hasBody) data = a1;
                                else params = a1;
                                break;
                            case 0: break;
                            default:
                                throw $resourceMinErr('badargs',
                                    'Expected up to 4 arguments [params, data, success, error], got {0} arguments',
                                    arguments.length);
                        }

                        var isInstanceCall = this instanceof Resource;
                        var value = isInstanceCall ? data : (action.isArray ? [] : new Resource(data));
                        var httpConfig = {};
                        var requestInterceptor = action.interceptor && action.interceptor.request || undefined;
                        var requestErrorInterceptor = action.interceptor && action.interceptor.requestError ||
                            undefined;
                        var responseInterceptor = action.interceptor && action.interceptor.response ||
                            defaultResponseInterceptor;
                        var responseErrorInterceptor = action.interceptor && action.interceptor.responseError ||
                            $q.reject;
                        var successCallback = onSuccess ? function(val) {
                            onSuccess(val, response.headers, response.status, response.statusText);
                        } : undefined;
                        var errorCallback = onError || undefined;
                        var timeoutDeferred;
                        var numericTimeoutPromise;
                        var response;

                        forEach(action, function(value, key) {
                            switch (key) {
                                default:
                                    httpConfig[key] = copy(value);
                                    break;
                                case 'params':
                                case 'isArray':
                                case 'interceptor':
                                case 'cancellable':
                                    break;
                            }
                        });

                        if (!isInstanceCall && cancellable) {
                            timeoutDeferred = $q.defer();
                            httpConfig.timeout = timeoutDeferred.promise;

                            if (numericTimeout) {
                                numericTimeoutPromise = $timeout(timeoutDeferred.resolve, numericTimeout);
                            }
                        }

                        if (hasBody) httpConfig.data = data;
                        route.setUrlParams(httpConfig,
                            extend({}, extractParams(data, action.params || {}), params),
                            action.url);

                        // Start the promise chain
                        var promise = $q.
                        resolve(httpConfig).
                        then(requestInterceptor).
                        catch(requestErrorInterceptor).
                        then($http);

                        promise = promise.then(function(resp) {
                            var data = resp.data;

                            if (data) {
                                // Need to convert action.isArray to boolean in case it is undefined
                                if (isArray(data) !== (!!action.isArray)) {
                                    throw $resourceMinErr('badcfg',
                                        'Error in resource configuration for action `{0}`. Expected response to ' +
                                        'contain an {1} but got an {2} (Request: {3} {4})', name, action.isArray ? 'array' : 'object',
                                        isArray(data) ? 'array' : 'object', httpConfig.method, httpConfig.url);
                                }
                                if (action.isArray) {
                                    value.length = 0;
                                    forEach(data, function(item) {
                                        if (typeof item === 'object') {
                                            value.push(new Resource(item));
                                        } else {
                                            // Valid JSON values may be string literals, and these should not be converted
                                            // into objects. These items will not have access to the Resource prototype
                                            // methods, but unfortunately there
                                            value.push(item);
                                        }
                                    });
                                } else {
                                    var promise = value.$promise;     // Save the promise
                                    shallowClearAndCopy(data, value);
                                    value.$promise = promise;         // Restore the promise
                                }
                            }

                            resp.resource = value;
                            response = resp;
                            return responseInterceptor(resp);
                        }, function(rejectionOrResponse) {
                            rejectionOrResponse.resource = value;
                            response = rejectionOrResponse;
                            return responseErrorInterceptor(rejectionOrResponse);
                        });

                        promise = promise['finally'](function() {
                            value.$resolved = true;
                            if (!isInstanceCall && cancellable) {
                                value.$cancelRequest = noop;
                                $timeout.cancel(numericTimeoutPromise);
                                timeoutDeferred = numericTimeoutPromise = httpConfig.timeout = null;
                            }
                        });

                        // Run the `success`/`error` callbacks, but do not let them affect the returned promise.
                        promise.then(successCallback, errorCallback);

                        if (!isInstanceCall) {
                            // we are creating instance / collection
                            // - set the initial promise
                            // - return the instance / collection
                            value.$promise = promise;
                            value.$resolved = false;
                            if (cancellable) value.$cancelRequest = cancelRequest;

                            return value;
                        }

                        // instance call
                        return promise;

                        function cancelRequest(value) {
                            promise.catch(noop);
                            if (timeoutDeferred !== null) {
                                timeoutDeferred.resolve(value);
                            }
                        }
                    };


                    Resource.prototype['$' + name] = function(params, success, error) {
                        if (isFunction(params)) {
                            error = success; success = params; params = {};
                        }
                        var result = Resource[name].call(this, params, this, success, error);
                        return result.$promise || result;
                    };
                });

                return Resource;
            }

            return resourceFactory;
        }];
    });


})(window, window.angular);
